import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { userStore } from '../../store';
import { Loader, showToast, dynamicIsland } from '../../components/shared';
import { ArrowLeft, Eye, EyeOff, User, Mail, Lock, CheckCircle2 } from 'lucide-react';

// Signup screen — v2 design (2026-05-30).
//
// Layout:
//   - Lavender wash background, matching Login + ForgotPassword.
//   - Back arrow top-left → /login.
//   - "Create Account" headline + subtitle.
//   - Fields: Full Name, Email, Password (with strength hint),
//     Confirm Password.
//   - Indigo "Create Account" button.
//   - "Already have an account? Login" link.
//
// Per Mike: no SSO buttons.
//
// Country is preserved server-side as the existing userStore.registerUser
// contract still requires it; we default to 'US' invisibly so the screen
// can match the simpler v2 form without churning the auth flow.

const BRAND = '#5B47E0';
const BRAND_DARK = '#4A37CC';
const TEXT_PRIMARY = '#0F172A';
const TEXT_MUTED = '#64748B';

const SignupScreen: React.FC = observer(() => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const passwordOk = password.length >= 8;
  const passwordsMatch = password.length > 0 && password === confirmPassword;

  const handleSignup = () => {
    if (!name.trim()) {
      showToast('Please enter your name', 'error');
      return;
    }
    if (!email.trim() || !emailRegex.test(email)) {
      showToast('Please enter a valid email', 'error');
      return;
    }
    if (!passwordOk) {
      showToast('Password must be at least 8 characters', 'error');
      return;
    }
    if (!passwordsMatch) {
      showToast('Passwords do not match', 'error');
      return;
    }

    userStore.registerUser(
      {
        name: name.trim(),
        email: email.trim(),
        country: 'US', // hidden default; user can change later in Profile
        password,
      },
      (success, message) => {
        if (success) {
          dynamicIsland.show({
            kind: 'success',
            title: 'Welcome to KleverBook',
            subtitle: name.trim(),
          });
          navigate('/app/home', { replace: true });
        } else {
          dynamicIsland.show({
            kind: 'error',
            title: 'Sign-up failed',
            subtitle: message || 'Please try again',
          });
        }
      },
    );
  };

  return (
    <div
      className="h-full relative overflow-y-auto"
      style={{
        background:
          'linear-gradient(180deg, #F8F5FE 0%, #EEEAFB 50%, #E5DFF9 100%)',
      }}
    >
      <Loader visible={userStore.regLoader} message="Creating your account..." />

      {/* Top bar with back arrow */}
      <div className="safe-area-top">
        <div className="px-5 pt-4 pb-2">
          <button
            onClick={() => navigate('/login')}
            aria-label="Back"
            className="w-10 h-10 rounded-full bg-white flex items-center justify-center transition-colors"
            style={{ border: '1px solid #E2DFF5' }}
          >
            <ArrowLeft className="w-5 h-5" style={{ color: TEXT_PRIMARY }} />
          </button>
        </div>
      </div>

      <div className="px-6 pt-2 pb-10 safe-area-bottom">
        {/* === Heading === */}
        <div className="text-center mb-8 max-w-sm mx-auto">
          <h1
            className="text-[30px] font-bold tracking-tight"
            style={{ color: TEXT_PRIMARY, lineHeight: 1.1 }}
          >
            Create Account
          </h1>
          <p
            className="text-[15px] mt-3"
            style={{ color: TEXT_MUTED }}
          >
            Join KleverBook and start your reading journey today.
          </p>
        </div>

        {/* === Form === */}
        <div className="w-full max-w-sm mx-auto space-y-4">
          <FieldGroup label="Full Name">
            <Field
              icon={<User className="w-[18px] h-[18px]" style={{ color: TEXT_MUTED }} />}
              type="text"
              value={name}
              onChange={setName}
              placeholder="Enter your full name"
              autoComplete="name"
            />
          </FieldGroup>

          <FieldGroup label="Email Address">
            <Field
              icon={<Mail className="w-[18px] h-[18px]" style={{ color: TEXT_MUTED }} />}
              type="email"
              value={email}
              onChange={setEmail}
              placeholder="Enter your email"
              autoComplete="email"
            />
          </FieldGroup>

          <FieldGroup label="Password">
            <Field
              icon={<Lock className="w-[18px] h-[18px]" style={{ color: TEXT_MUTED }} />}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={setPassword}
              placeholder="Create a password"
              autoComplete="new-password"
              trailing={
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword
                    ? <EyeOff className="w-[18px] h-[18px]" style={{ color: TEXT_MUTED }} />
                    : <Eye className="w-[18px] h-[18px]" style={{ color: TEXT_MUTED }} />}
                </button>
              }
            />
            <div className="flex items-center gap-1.5 mt-1.5 pl-1">
              <CheckCircle2
                className="w-[14px] h-[14px]"
                style={{ color: passwordOk ? '#16A34A' : '#CBD5E1' }}
              />
              <span
                className="text-[12px]"
                style={{ color: passwordOk ? '#16A34A' : TEXT_MUTED }}
              >
                At least 8 characters
              </span>
            </div>
          </FieldGroup>

          <FieldGroup label="Confirm Password">
            <Field
              icon={<Lock className="w-[18px] h-[18px]" style={{ color: TEXT_MUTED }} />}
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={setConfirmPassword}
              placeholder="Confirm your password"
              autoComplete="new-password"
              trailing={
                <button
                  type="button"
                  onClick={() => setShowConfirm((s) => !s)}
                  aria-label={showConfirm ? 'Hide password' : 'Show password'}
                >
                  {showConfirm
                    ? <EyeOff className="w-[18px] h-[18px]" style={{ color: TEXT_MUTED }} />
                    : <Eye className="w-[18px] h-[18px]" style={{ color: TEXT_MUTED }} />}
                </button>
              }
            />
          </FieldGroup>

          <button
            onClick={handleSignup}
            className="w-full h-12 rounded-2xl text-white text-[16px] font-semibold transition-transform active:scale-[0.98] mt-3"
            style={{
              background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
              boxShadow: '0 12px 28px -8px rgba(91, 71, 224, 0.5)',
            }}
          >
            Create Account
          </button>
        </div>

        {/* === Login footer === */}
        <div className="mt-8 text-center">
          <span className="text-[14px]" style={{ color: TEXT_MUTED }}>
            Already have an account?{' '}
          </span>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-[14px] font-semibold"
            style={{ color: BRAND }}
          >
            Login
          </button>
        </div>
      </div>
    </div>
  );
});

/* ====================================================================
 *  Field primitives
 * ==================================================================== */

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label
        className="block text-[14px] font-medium mb-2 pl-1"
        style={{ color: TEXT_PRIMARY }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function Field({
  icon,
  type,
  value,
  onChange,
  placeholder,
  autoComplete,
  trailing,
}: {
  icon: React.ReactNode;
  type: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  autoComplete?: string;
  trailing?: React.ReactNode;
}) {
  return (
    <div
      className="relative h-12 rounded-2xl flex items-center px-4"
      style={{
        background: '#FFFFFF',
        border: '1px solid #E2DFF5',
      }}
    >
      <span className="mr-3 flex-shrink-0">{icon}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="flex-1 bg-transparent outline-none text-[15px]"
        style={{ color: TEXT_PRIMARY }}
      />
      {trailing && <span className="ml-2 flex-shrink-0">{trailing}</span>}
    </div>
  );
}

export default SignupScreen;
